import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Ticket, Plus, CheckCircle2, Clock, Circle, XCircle, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import useFocusItem from '@/hooks/useFocusItem';

const T = {
  fr: {
    title: 'Tickets & Suggestions',
    subtitle: 'Signalez un bug, suggérez une fonctionnalité ou posez une question. L\'équipe ProdLink vous répondra.',
    new: 'Nouveau ticket',
    none: "Vous n'avez pas encore de ticket. Cliquez sur \"Nouveau ticket\" pour démarrer.",
    titleField: 'Titre',
    descField: 'Description',
    type: 'Type',
    priority: 'Priorité',
    submit: 'Envoyer',
    cancel: 'Annuler',
    status: { open: 'Ouvert', in_progress: 'En cours', resolved: 'Résolu', closed: 'Fermé' },
    types: { bug: 'Bug', feature: 'Fonctionnalité', improvement: 'Amélioration', question: 'Question' },
    priorities: { critical: 'Critique', high: 'Haute', medium: 'Moyenne', low: 'Faible' },
    response: 'Réponse ProdLink',
    sent: 'Ticket envoyé. Merci pour votre contribution !',
    error: 'Erreur',
    required: 'Titre et description requis',
    onlyOpen: 'Modifiable uniquement quand statut = Ouvert',
    privacy: 'Vous voyez uniquement vos propres tickets.',
  },
  en: {
    title: 'Tickets & Suggestions',
    subtitle: 'Report a bug, suggest a feature, or ask a question. The ProdLink team will reply.',
    new: 'New ticket',
    none: 'You have no tickets yet. Click "New ticket" to start.',
    titleField: 'Title',
    descField: 'Description',
    type: 'Type',
    priority: 'Priority',
    submit: 'Send',
    cancel: 'Cancel',
    status: { open: 'Open', in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed' },
    types: { bug: 'Bug', feature: 'Feature', improvement: 'Improvement', question: 'Question' },
    priorities: { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' },
    response: 'ProdLink response',
    sent: 'Ticket sent. Thanks for your contribution!',
    error: 'Error',
    required: 'Title and description are required',
    onlyOpen: 'Editable only while status = Open',
    privacy: 'You only see your own tickets.',
  },
};

const PRIORITY_COLOR = {
  critical: 'border-rose-500/40 bg-rose-500/5 text-rose-300 border',
  high:     'border-amber-500/40 bg-amber-500/5 text-amber-300 border',
  medium:   'border-sky-500/40 bg-sky-500/5 text-sky-300 border',
  low:      'border-zinc-700 bg-zinc-800/50 text-zinc-400 border',
};
const STATUS_ICON = {
  open: { icon: Circle, color: 'text-sky-400' },
  in_progress: { icon: Clock, color: 'text-amber-400' },
  resolved: { icon: CheckCircle2, color: 'text-emerald-400' },
  closed: { icon: XCircle, color: 'text-zinc-500' },
};

export default function SubcontractorTicketsPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
  const t = T[lang];

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  useFocusItem(tickets);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets');
      setTickets(data);
    } catch (e) { toast.error(t.error); }
    finally { setLoading(false); }
  }, [t.error]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {t.title}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{t.subtitle}</p>
          <p className="text-[10px] text-zinc-600 mt-1">🔒 {t.privacy}</p>
        </div>
        <Button data-testid="new-ticket-btn" onClick={() => setNewOpen(true)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
          <Plus className="w-4 h-4 mr-2" /> {t.new}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">…</div>
      ) : tickets.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="text-center py-16 text-zinc-500">{t.none}</CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map(tk => {
            const StatusIcon = STATUS_ICON[tk.status]?.icon || Circle;
            const isOpen = expanded[tk.id];
            return (
              <Card key={tk.id} data-focus-id={tk.id} className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid={`sub-ticket-${tk.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-zinc-100">{tk.title}</h3>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-400`}>{t.types[tk.type]}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${PRIORITY_COLOR[tk.priority]}`}>{t.priorities[tk.priority]}</span>
                          <span className={`inline-flex items-center gap-1 text-[10px] ${STATUS_ICON[tk.status]?.color}`}>
                            <StatusIcon className="w-3 h-3" /> {t.status[tk.status]}
                          </span>
                        </div>
                      </div>
                      <p className={`text-xs text-zinc-400 whitespace-pre-line ${isOpen ? '' : 'line-clamp-2'}`}>{tk.description}</p>
                      {(tk.description.length > 140 || tk.admin_response) && (
                        <button onClick={() => toggle(tk.id)} className="text-[10px] text-amber-400 hover:underline mt-1 flex items-center gap-0.5">
                          {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {isOpen ? (lang === 'fr' ? 'Réduire' : 'Collapse') : (lang === 'fr' ? 'Voir plus' : 'Show more')}
                        </button>
                      )}
                      {isOpen && tk.admin_response && (
                        <div className="mt-2 bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-2.5 text-xs text-emerald-200">
                          <MessageSquare className="w-3 h-3 inline mr-1" /><strong>{t.response} :</strong> {tk.admin_response}
                        </div>
                      )}
                      <p className="text-[10px] text-zinc-600 mt-2">
                        {new Date(tk.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {newOpen && <NewTicketDialog open={newOpen} onOpenChange={setNewOpen} onSaved={fetchTickets} lang={lang} t={t} />}
    </div>
  );
}

function NewTicketDialog({ open, onOpenChange, onSaved, lang, t }) {
  const [form, setForm] = useState({ title: '', description: '', type: 'feature', priority: 'medium' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error(t.required);
      return;
    }
    setSaving(true);
    try {
      await api.post('/tickets', { ...form, lang });
      toast.success(t.sent);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t.error);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl" data-testid="sub-ticket-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
            <Ticket className="w-5 h-5 text-amber-400" /> {t.new}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">{t.subtitle}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-zinc-400">{t.titleField}</Label>
            <Input data-testid="sub-ticket-title" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">{t.descField}</Label>
            <Textarea data-testid="sub-ticket-description" rows={5} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">{t.type}</Label>
              <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="sub-ticket-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">{Object.entries(t.types).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">{t.priority}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm mt-1" data-testid="sub-ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">{Object.entries(t.priorities).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-300 rounded-sm">{t.cancel}</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium" data-testid="sub-save-ticket-btn">
            {saving ? '…' : t.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
